import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2, Building2, Users, ArrowLeft, Rocket, ShieldCheck, CheckCircle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO_URL = "https://customer-assets.emergentagent.com/job_a72ee028-442b-44ea-ad3d-be1abd62a5cf/artifacts/zdjtc3rc_Logo_Falo_com_o_dono-removebg-preview.png";

const Login = () => {
  const [searchParams] = useSearchParams();
  const initialView = searchParams.get('view') === 'owner' ? 'owner' : 'hub';
  const [view, setView] = useState(initialView);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Login realizado com sucesso!');
      navigate('/dashboard');
    } catch (error) {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      if (status === 401) {
        toast.error(
          'E-mail ou senha incorretos. Confira a área correta: Dono usa esta tela; Afiliado em /afiliados/login; Parceiro em /parceiro/acesso.',
          { duration: 7000 }
        );
      } else {
        toast.error(detail || 'Erro ao fazer login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchAccount = async () => {
    await logout();
    setView('owner');
    setEmail('');
    setPassword('');
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) { toast.error('Digite seu e-mail'); return; }
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail, type: 'owner' });
      setForgotSent(true);
      toast.success('E-mail de recuperação enviado!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar e-mail');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A2647] flex flex-col items-center justify-center p-4" data-testid="login-page">
      {/* Background */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/">
            <img src={LOGO_URL} alt="Falo Com O Dono" className="h-28 w-auto object-contain mx-auto" />
          </Link>
          <h1 className="text-white font-bold text-2xl mt-3" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            {view === 'hub' ? 'Central de Acesso' : 'Area do Dono'}
          </h1>
          {view === 'hub' && (
            <p className="text-slate-400 text-sm mt-1">Escolha seu perfil para continuar</p>
          )}
        </div>

        {view === 'hub' ? (
          /* ==================== HUB VIEW — 4 BOTÕES ==================== */
          <div className="space-y-3">
            {/* 1. Começar Grátis */}
            <Link to="/register" className="block" data-testid="hub-register-link">
              <Card className="border-0 shadow-2xl bg-[#C5A059] hover:bg-[#C5A059]/90 transition-all cursor-pointer group">
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center shrink-0">
                    <Rocket className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-bold text-base">Começar Grátis</p>
                    <p className="text-white/70 text-xs">Cadastre sua empresa agora</p>
                  </div>
                </CardContent>
              </Card>
            </Link>

            {/* 2. Área do Dono */}
            <Card
              className="border-0 shadow-2xl bg-white/95 backdrop-blur-sm hover:shadow-[0_8px_40px_rgba(197,160,89,0.15)] transition-all cursor-pointer group"
              onClick={() => setView('owner')}
              data-testid="hub-owner-login"
            >
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 bg-[#0A2647] rounded-xl flex items-center justify-center shrink-0">
                  <Building2 className="w-6 h-6 text-[#C5A059]" />
                </div>
                <div className="flex-1">
                  <p className="text-[#0A2647] font-bold text-base">Area do Dono</p>
                  <p className="text-slate-500 text-xs">Acesse o painel da sua empresa</p>
                </div>
              </CardContent>
            </Card>

            {/* 3. Área do Afiliado */}
            <Link to="/afiliados/login" className="block" data-testid="hub-affiliate-login">
              <Card className="border-0 shadow-2xl bg-white/95 backdrop-blur-sm hover:shadow-[0_8px_40px_rgba(197,160,89,0.15)] transition-all cursor-pointer group">
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#C5A059]/10 rounded-xl flex items-center justify-center shrink-0">
                    <Users className="w-6 h-6 text-[#C5A059]" />
                  </div>
                  <div className="flex-1">
                    <p className="text-[#0A2647] font-bold text-base">Area do Afiliado</p>
                    <p className="text-slate-500 text-xs">Login ou cadastro de parceiro</p>
                  </div>
                </CardContent>
              </Card>
            </Link>

            {/* 4. Área Gestor Master */}
            <Link to="/master" className="block" data-testid="hub-master-link">
              <Card className="border-0 shadow-lg bg-white/10 backdrop-blur-sm hover:bg-white/15 transition-all cursor-pointer group">
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center shrink-0">
                    <ShieldCheck className="w-6 h-6 text-slate-300" />
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-bold text-base">Area Gestor Master</p>
                    <p className="text-slate-400 text-xs">Acesso administrativo</p>
                  </div>
                </CardContent>
              </Card>
            </Link>
          </div>
        ) : (
          /* ==================== OWNER LOGIN FORM ==================== */
          <Card className="border-0 shadow-2xl bg-white/95 backdrop-blur-sm">
            <CardContent className="p-6">
              <button
                onClick={() => { setView('hub'); setForgotMode(false); setForgotSent(false); }}
                className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-4 transition-colors"
                data-testid="back-to-hub"
              >
                <ArrowLeft className="w-4 h-4" />Voltar
              </button>

              {user && !forgotMode && (
                <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid="already-logged-in-banner">
                  <p className="text-xs text-amber-800 mb-2">
                    Voce ja esta logado como <strong>{user.email}</strong>.
                  </p>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => navigate('/dashboard')}
                      className="flex-1 h-8 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg"
                      data-testid="go-to-dashboard-btn"
                    >
                      Ir para meu painel
                    </Button>
                    <Button
                      onClick={handleSwitchAccount}
                      variant="outline"
                      className="flex-1 h-8 text-xs font-semibold rounded-lg border-amber-300 text-amber-800 hover:bg-amber-100"
                      data-testid="switch-account-btn"
                    >
                      Sair e usar outra conta
                    </Button>
                  </div>
                </div>
              )}

              {forgotMode ? (
                forgotSent ? (
                  <div className="text-center py-4">
                    <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-7 h-7 text-emerald-600" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-2">E-mail Enviado!</h3>
                    <p className="text-sm text-slate-500 mb-4">Verifique sua caixa de entrada e siga as instrucoes para redefinir sua senha.</p>
                    <button onClick={() => { setForgotMode(false); setForgotSent(false); }} className="text-[#C5A059] font-semibold text-sm hover:underline" data-testid="back-to-login-btn">
                      Voltar ao Login
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    <div className="text-center mb-2">
                      <h3 className="text-lg font-bold text-slate-900">Recuperar Senha</h3>
                      <p className="text-sm text-slate-500">Digite seu e-mail para receber o link de recuperacao</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="forgot-email" className="text-sm font-medium">E-mail</Label>
                      <Input
                        id="forgot-email" type="email" placeholder="seu@email.com"
                        value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)}
                        required className="h-11" data-testid="forgot-email-input"
                      />
                    </div>
                    <Button type="submit" className="w-full bg-[#C5A059] hover:bg-[#C5A059]/90 text-[#0A2647] font-bold h-11 rounded-xl" disabled={forgotLoading} data-testid="forgot-submit-btn">
                      {forgotLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Enviando...</> : 'Enviar Link de Recuperacao'}
                    </Button>
                    <button type="button" onClick={() => setForgotMode(false)} className="w-full text-sm text-slate-500 hover:text-slate-700 mt-2" data-testid="cancel-forgot-btn">
                      Voltar ao Login
                    </button>
                  </form>
                )
              ) : (
                <>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-sm font-medium">E-mail</Label>
                      <Input
                        id="email" type="email" placeholder="seu@email.com"
                        value={email} onChange={(e) => setEmail(e.target.value)}
                        required className="h-11" data-testid="login-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="password" className="text-sm font-medium">Senha</Label>
                        <button type="button" onClick={() => setForgotMode(true)} className="text-xs text-[#C5A059] hover:underline font-medium" data-testid="forgot-password-link">
                          Esqueci minha senha
                        </button>
                      </div>
                      <Input
                        id="password" type="password" placeholder="Sua senha"
                        value={password} onChange={(e) => setPassword(e.target.value)}
                        required className="h-11" data-testid="login-password-input"
                      />
                    </div>
                    <Button type="submit" className="w-full bg-[#0A2647] hover:bg-[#0A2647]/90 text-white font-semibold h-11 rounded-xl" disabled={loading} data-testid="login-submit-btn">
                      {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Entrando...</> : 'Entrar'}
                    </Button>
                  </form>
                  <p className="mt-4 text-center text-sm text-slate-500">
                    Não tem conta? <Link to="/register" className="text-[#C5A059] font-semibold hover:underline" data-testid="register-link">Cadastre-se grátis</Link>
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Login;
